import { create } from 'zustand';
import {
  api,
  clearSession,
  getRefreshToken,
  refreshAccessToken,
  setAccessToken,
  setRefreshToken,
} from '@/lib/api/client';

export interface PanelUser {
  id: number;
  name: string;
  email: string;
  role: string;
  companyId: number | null;
}

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  user: PanelUser;
  company: { id: number; name: string; timezone: string } | null;
}

/**
 * Session state for the panel.
 *
 * Token storage and rotation live entirely in `@/lib/api/client` — including
 * the sessionStorage gap documented there. This store owns who is signed in,
 * not how the tokens are kept. They were split across both files before, with
 * two different refresh implementations that disagreed about whether the API
 * used a cookie or a request body; only one of them worked.
 */
interface AuthState {
  user: PanelUser | null;
  company: { id: number; name: string; timezone: string } | null;
  status: 'unknown' | 'authenticated' | 'anonymous';
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restore: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  company: null,
  status: 'unknown',

  async login(email, password) {
    const { data } = await api.post<LoginResponse>('/auth/login', { email, password });
    setAccessToken(data.accessToken);
    setRefreshToken(data.refreshToken);
    set({ user: data.user, company: data.company, status: 'authenticated' });
  },

  async logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // Already-expired tokens make logout fail; clearing locally is what
      // matters, and the server-side token expires on its own.
    }
    clearSession();
    set({ user: null, company: null, status: 'anonymous' });
  },

  /**
   * Called once at startup. Exchanges the stored refresh token for a fresh
   * pair; the rotation means a stolen refresh token is single-use.
   *
   * Goes through the client's single-flight refresh rather than issuing its
   * own request. That is what makes it safe to call twice — which React
   * StrictMode does on every mount in development. The previous version
   * refreshed here directly, so the double-invoke presented the same single-use
   * token twice, the server correctly read that as a replay, and it revoked
   * every session the user had. Signing in and reloading the page was enough to
   * trigger it.
   */
  async restore() {
    if (!getRefreshToken()) {
      set({ status: 'anonymous' });
      return;
    }

    if (!(await refreshAccessToken())) {
      // Deliberately does NOT clear the stored token. The client discards it
      // itself on a definitive 401/403; anything else — a 5xx, a dropped
      // connection, a 429 — is transient, and throwing away a still-valid
      // refresh token would turn a momentary blip into a forced re-login.
      set({ status: 'anonymous' });
      return;
    }

    try {
      const me = await api.get<{ user: PanelUser; company: AuthState['company'] }>('/auth/me');
      set({ user: me.data.user, company: me.data.company, status: 'authenticated' });
    } catch {
      clearSession();
      set({ status: 'anonymous' });
    }
  },
}));
